import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Share2, Users, Trophy, ArrowLeft, Sparkles } from "lucide-react";

import { api } from "@/lib/api";


export default function Referrals() {
  const [me, setMe] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [meRes, lbRes] = await Promise.all([
        api.get("/referrals/me"),
        api.get("/referrals/leaderboard"),
      ]);
      setMe(meRes.data);
      setLeaderboard(lbRes.data.leaderboard || []);
    } catch (err) {
      toast.error("Couldn't load referrals");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const shareUrl = me?.code
    ? `${window.location.origin}/signup?ref=${me.code}`
    : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch { toast.error("Couldn't copy — long-press to select"); }
  };

  const nativeShare = async () => {
    if (!navigator.share) { copyLink(); return; }
    try {
      await navigator.share({
        title: "MQL4Forge — build MetaTrader robots without code",
        text: "Forge trading bots visually or with AI. Join with my link:",
        url: shareUrl,
      });
    } catch { /* user cancelled */ }
  };

  if (loading) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="referrals-page">
      <Link to="/dashboard" data-testid="referrals-back"
        className="inline-flex items-center gap-1 text-[10px] font-mono text-[var(--text-muted)] hover:text-[var(--accent)] mb-3">
        <ArrowLeft size={11}/> Dashboard
      </Link>

      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} className="text-[var(--accent)]"/>
        <span className="chip">REFERRALS · BETA</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl text-white mb-2">Invite fellow traders.</h1>
      <p className="text-sm text-[var(--text-secondary)] mb-8 max-w-lg">
        Share your link. Anyone who signs up through it will be tracked here.
        Rewards are coming — early referrers will be first in line.
      </p>

      {/* My share card */}
      <section className="card-sharp p-5 mb-6" data-testid="referrals-my-card">
        <div className="flex items-center gap-2 mb-3">
          <Users size={14} className="text-[var(--accent)]"/>
          <h3 className="font-display text-lg text-white flex-1">Your link</h3>
          <span className="chip" data-testid="referrals-count">
            {me?.count ?? 0} joined
          </span>
        </div>

        <div className="flex flex-col md:flex-row gap-2 mb-3">
          <div className="flex-1 border border-white/10 rounded px-3 py-2 bg-black/40 font-mono text-xs overflow-x-auto whitespace-nowrap"
            data-testid="referrals-share-url">
            {shareUrl}
          </div>
          <button data-testid="referrals-copy-btn" onClick={copyLink}
            className="btn-primary text-xs px-4 flex items-center gap-2 justify-center">
            <Copy size={12}/> Copy
          </button>
          <button data-testid="referrals-share-btn" onClick={nativeShare}
            className="border border-white/10 rounded px-4 py-2 text-xs font-mono hover:border-[var(--accent)] hover:text-[var(--accent)] flex items-center gap-2 justify-center">
            <Share2 size={12}/> Share
          </button>
        </div>

        <div className="text-[10px] font-mono text-[var(--text-muted)]">
          Your code: <span className="text-[var(--accent)]" data-testid="referrals-code">{me?.code || "—"}</span>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="card-sharp p-5" data-testid="referrals-leaderboard">
        <div className="flex items-center gap-2 mb-4">
          <Trophy size={14} className="text-[var(--accent)]"/>
          <h3 className="font-display text-lg text-white flex-1">Top referrers</h3>
          <span className="chip">top 10</span>
        </div>

        {leaderboard.length === 0 ? (
          <div className="text-center py-8">
            <Trophy size={22} className="mx-auto text-[var(--text-muted)] mb-2"/>
            <p className="text-xs font-mono text-[var(--text-muted)]">
              No referrals yet. Be the first to invite a trader and land #1 here.
            </p>
          </div>
        ) : (
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-[var(--text-muted)] border-b border-white/10">
                <th className="text-left py-2 w-10">#</th>
                <th className="text-left">Trader</th>
                <th className="text-right">Referrals</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((r, i) => (
                <tr key={`${r.handle}-${i}`} className="border-b border-white/5"
                  data-testid={`referrals-lb-row-${i}`}>
                  <td className="py-2 text-[var(--text-muted)]">
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}
                  </td>
                  <td className="text-white">{r.handle}</td>
                  <td className="text-right text-[var(--accent)]">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
